type ThemeColors = {
  base_color?: string;
  secondary_color?: string;
  footer_background?: string;
  text?: {
    dark?: string;
    light?: string;
  };
  background?: {
    light?: string;
  };
};

type ThemeSpacing = {
  half_gap?: string;
  normal_gap?: string;
  double_gap?: string;
  center_padding_large?: string;
};

type ThemeLayout = {
  navbar_height?: string;
  card_img_height_mobile?: string;
  card_img_height_desktop?: string;
  card_img_width_desktop?: string;
};

type ThemeShadows = {
  smallShadow?: string;
  normalShadow?: string;
  hoverShadow?: string;
};

type ThemeTypography = {
  fontFamily?: string;
};

type ThemeContract = {
  colors?: ThemeColors;
  spacing?: ThemeSpacing;
  layout?: ThemeLayout;
  shadows?: ThemeShadows;
  border_radius?: string | number;
  typography?: ThemeTypography;
};

function isThemeContract(value: ThemeColors | ThemeContract): value is ThemeContract {
  return !!(
    (value as ThemeContract).colors ||
    (value as ThemeContract).spacing ||
    (value as ThemeContract).layout ||
    (value as ThemeContract).shadows ||
    (value as ThemeContract).typography ||
    (value as ThemeContract).border_radius !== undefined
  );
}

export function applyThemeCssVars(themeOrColors: ThemeColors | ThemeContract) {
  const root = document.documentElement;
  const contract = isThemeContract(themeOrColors) ? themeOrColors : { colors: themeOrColors };
  const colors = contract.colors;

  if (colors?.base_color) {
    root.style.setProperty("--brand-base-color", colors.base_color);
  }

  if (colors?.secondary_color) {
    root.style.setProperty("--brand-secondary-color", colors.secondary_color);
  }

  if (colors?.footer_background) {
    root.style.setProperty("--brand-footer-bg", colors.footer_background);
  }

  if (colors?.text?.dark) {
    root.style.setProperty("--brand-text-dark", colors.text.dark);
  }

  if (colors?.text?.light) {
    root.style.setProperty("--brand-text-light", colors.text.light);
  }

  if (colors?.background?.light) {
    root.style.setProperty("--brand-bg-light", colors.background.light);
  }

  if (contract.spacing?.half_gap) {
    root.style.setProperty("--brand-half-gap", contract.spacing.half_gap);
  }
  if (contract.spacing?.normal_gap) {
    root.style.setProperty("--brand-normal-gap", contract.spacing.normal_gap);
  }
  if (contract.spacing?.double_gap) {
    root.style.setProperty("--brand-double-gap", contract.spacing.double_gap);
  }
  if (contract.spacing?.center_padding_large) {
    root.style.setProperty("--brand-center-padding-large", contract.spacing.center_padding_large);
  }

  if (contract.layout?.navbar_height) {
    root.style.setProperty("--brand-navbar-height", contract.layout.navbar_height);
  }
  if (contract.layout?.card_img_height_mobile) {
    root.style.setProperty(
      "--brand-card-img-height-mobile",
      contract.layout.card_img_height_mobile,
    );
  }
  if (contract.layout?.card_img_height_desktop) {
    root.style.setProperty(
      "--brand-card-img-height-desktop",
      contract.layout.card_img_height_desktop,
    );
  }
  if (contract.layout?.card_img_width_desktop) {
    root.style.setProperty(
      "--brand-card-img-width-desktop",
      contract.layout.card_img_width_desktop,
    );
  }

  if (contract.shadows?.smallShadow) {
    root.style.setProperty("--brand-shadow-small", contract.shadows.smallShadow);
  }
  if (contract.shadows?.normalShadow) {
    root.style.setProperty("--brand-shadow-normal", contract.shadows.normalShadow);
  }
  if (contract.shadows?.hoverShadow) {
    root.style.setProperty("--brand-shadow-hover", contract.shadows.hoverShadow);
  }

  if (typeof contract.border_radius === "string" || typeof contract.border_radius === "number") {
    root.style.setProperty("--brand-border-radius", String(contract.border_radius));
  }

  if (contract.typography?.fontFamily) {
    root.style.setProperty("--brand-font-family-base", contract.typography.fontFamily);
  }

  // Shared neutral panel background used by multiple list pages.
  root.style.setProperty("--brand-surface-muted", "#FAFAFA");
}
